import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import appApi from '../../../../api/app-api';
import { ChartOfAccountDetailsEntity } from '../../../../api/types/settings/accounting/chart-of-accounts/chart-of-account-details.type';
import { JournalEntryStatus } from '../../../../api/types/accounting/journal-entries/journal-entry-entity';
import { PendingJournalEntryWithLinesEntity } from '../../../../api/types/accounting/journal-entries/pending-journal-entry-with-lines-entity';
import { PaymentMethodsDb } from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { WarningDialog } from '../../../../component/ui/warning-dialog/warning-dialog';
import { formatDate } from '../../../../utils/date-format';
import { createJournalEntryReportOperation } from '../operations/create-journal-entry-report-operation';
import { getPendingJournalEntriesForReportOperation } from '../operations/get-pending-journal-entries-for-report-operation';
import { JournalEntriesWaitingForReportFilter } from './components/journal-entries-waiting-for-report-filter';
import {
  JournalEntriesWaitingForReportFilterType,
  defaultJournalEntriesWaitingForReportFilter,
} from './journal-entries-waiting-for-report-filter.type';

const DEFAULT_OFFSET = 0;
const DEFAULT_LIMIT = 20;

const statusLabel = (status: JournalEntryStatus): string => {
  switch (status) {
    case JournalEntryStatus.PENDING:
      return 'ממתין לדיווח';
    case JournalEntryStatus.EXPORTED:
      return 'דווח';
    default:
      return status;
  }
};

const statusSeverity = (
  status: JournalEntryStatus,
): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case JournalEntryStatus.PENDING:
      return 'warning';
    case JournalEntryStatus.EXPORTED:
      return 'success';
    default:
      return 'info';
  }
};

const paymentMethodLabel = (paymentMethod: PaymentMethodsDb): string => {
  switch (paymentMethod) {
    case PaymentMethodsDb.CreditCard:
      return 'כרטיס אשראי';
    case PaymentMethodsDb.Cash:
      return 'מזומן';
    case PaymentMethodsDb.Discount:
      return 'הנחה';
    case PaymentMethodsDb.BankTransfer:
      return 'העברה בנקאית';
    case PaymentMethodsDb.Scholarship:
      return 'מלגה';
    case PaymentMethodsDb.Check:
      return 'המחאה';
    default:
      return paymentMethod;
  }
};

const sourceLabel = (row: PendingJournalEntryWithLinesEntity): string => {
  if (row.userPaymentId !== null) return 'תשלום סטודנט';
  if (row.userPaymentCheckId !== null) return 'המחאה סטודנט';
  if (row.crmPaymentId !== null) return 'תשלום CRM';
  if (row.crmCheckId !== null) return 'המחאה CRM';
  if (row.depositId !== null) return 'הפקדה';
  if (row.depositPaymentId !== null) return 'תשלום בהפקדה';
  return '—';
};

const totalDebit = (
  lines: PendingJournalEntryWithLinesEntity['lines'],
): number =>
  (lines ?? []).reduce(
    (sum, l) => sum + (typeof l.debit === 'number' ? l.debit : 0),
    0,
  );

export const JournalEntriesWaitingForReportPage = () => {
  const [data, setData] = useState<PendingJournalEntryWithLinesEntity[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const [offset, setOffset] = useState<number>(DEFAULT_OFFSET);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);

  const [filters, setFilters] =
    useState<JournalEntriesWaitingForReportFilterType>(
      defaultJournalEntriesWaitingForReportFilter(),
    );

  const [chartOfAccounts, setChartOfAccounts] = useState<
    ChartOfAccountDetailsEntity[]
  >([]);

  const [selectedRows, setSelectedRows] = useState<
    PendingJournalEntryWithLinesEntity[]
  >([]);

  const [showReportWarning, setShowReportWarning] = useState<boolean>(false);
  const [reportingLoading, setReportingLoading] = useState<boolean>(false);

  const runSearch = async (
    nextFilters: JournalEntriesWaitingForReportFilterType,
    nextOffset: number,
    nextLimit: number,
  ) => {
    // Clear stale selection when the underlying page/data changes - otherwise
    // checkbox state can desync from the rows actually visible.
    setSelectedRows([]);
    await getPendingJournalEntriesForReportOperation(
      {
        offset: nextOffset,
        limit: nextLimit,
        fromDate: nextFilters.fromDate,
        toDate: nextFilters.toDate,
        paymentMethod: nextFilters.paymentMethod,
        chartOfAccountIds: nextFilters.chartOfAccountIds,
      },
      setData,
      setTotal,
      setLoading,
    );
  };

  const handleReportClick = () => {
    if (selectedRows.length === 0) return;
    setShowReportWarning(true);
  };

  const handleReportConfirm = async () => {
    const entriesIds = selectedRows.map((row) => row.id);
    await createJournalEntryReportOperation(
      entriesIds,
      setReportingLoading,
      async () => {
        setShowReportWarning(false);
        setSelectedRows([]);
        await runSearch(filters, offset, limit);
      },
    );
  };

  const fetchChartOfAccounts = async () => {
    try {
      const res = await appApi.getChartOfAccountsList();
      const list = res.data.body ?? [];
      setChartOfAccounts(list.filter((acc) => acc.isActive));
    } catch (e) {
      // silent - the chart-of-accounts multiselect just stays empty if this fails
    }
  };

  useEffect(() => {
    runSearch(filters, offset, limit);
    fetchChartOfAccounts();
  }, []);

  const handleSearch = async () => {
    setOffset(DEFAULT_OFFSET);
    await runSearch(filters, DEFAULT_OFFSET, limit);
  };

  const handleClear = () => {
    setFilters(defaultJournalEntriesWaitingForReportFilter());
  };

  const onPageChange = (event: DataTablePageEvent) => {
    const newOffset = event.first;
    const newLimit = event.rows;
    setOffset(newOffset);
    setLimit(newLimit);
    runSearch(filters, newOffset, newLimit);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '12px',
        direction: 'rtl',
      }}
    >
      <JournalEntriesWaitingForReportFilter
        filters={filters}
        chartOfAccounts={chartOfAccounts}
        loading={loading}
        onChange={setFilters}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              color: '#4b5563',
              textAlign: 'right',
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <span>סה״כ פקודות יומן: {total.toLocaleString()}</span>
            {selectedRows.length > 0 && (
              <span
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  color: '#065f46',
                  fontWeight: 600,
                }}
              >
                נבחרו: {selectedRows.length}
              </span>
            )}
          </div>

          {selectedRows.length > 0 && (
            <Button
              icon="pi pi-check"
              label="דווח על הנבחרות"
              onClick={handleReportClick}
              disabled={reportingLoading}
              severity="success"
            />
          )}
        </div>

        <DataTable
          value={data}
          rowHover
          loading={loading}
          paginator
          rows={limit}
          first={offset}
          totalRecords={total}
          onPage={onPageChange}
          lazy={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו פקודות יומן</div>
          }
          dataKey="id"
          tableStyle={{ direction: 'rtl' }}
          selectionMode="multiple"
          selection={selectedRows}
          onSelectionChange={(e) =>
            setSelectedRows(e.value as PendingJournalEntryWithLinesEntity[])
          }
        >
          <Column
            selectionMode="multiple"
            headerStyle={{ width: '3rem' }}
            style={{ width: '3rem', textAlign: 'right' }}
          />
          {/*<Column
            field="id"
            header="מזהה"
            style={{ textAlign: 'right', width: '90px' }}
            headerStyle={{ textAlign: 'right' }}
          />*/}
          {/*          <Column
            field="entryDate"
            header="תאריך פקודה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              row.entryDate ? formatDate(row.entryDate as Date) : 'חסר'
            }
          />*/}
          <Column
            field="paymentMethodType"
            header="אמצעי תשלום"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              paymentMethodLabel(row.paymentMethodType)
            }
          />
          <Column
            field="status"
            header="סטטוס"
            style={{ textAlign: 'right', width: '130px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) => (
              <Tag
                value={statusLabel(row.status)}
                severity={statusSeverity(row.status)}
              />
            )}
          />
          <Column
            header="סכום"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              totalDebit(row.lines).toFixed(2)
            }
          />
          <Column
            header="מקור"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) => sourceLabel(row)}
          />
          <Column
            field="createdAt"
            header="תאריך יצירה"
            style={{ textAlign: 'right', width: '140px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
            }
          />
          <Column
            field="createdByName"
            header="נוצר על ידי"
            style={{ textAlign: 'right', width: '160px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              row.createdByName?.trim() ? row.createdByName : 'חסר'
            }
          />
          <Column
            header="מספר מסמך"
            style={{ textAlign: 'right', width: '120px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              typeof row.accountingDocument?.docNumber === 'number'
                ? row.accountingDocument.docNumber
                : 'חסר'
            }
          />
          <Column
            header="קובץ"
            style={{ textAlign: 'right', width: '100px' }}
            headerStyle={{ textAlign: 'right' }}
            body={(row: PendingJournalEntryWithLinesEntity) =>
              row.accountingDocument?.fileUrl ? (
                <a
                  href={row.accountingDocument.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#2563eb',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <i className="pi pi-external-link" />
                  פתח
                </a>
              ) : (
                <span style={{ color: '#9ca3af' }}>חסר</span>
              )
            }
          />
        </DataTable>
      </div>

      <WarningDialog
        header="אישור דיווח פקודות יומן"
        isVisible={showReportWarning}
        onHide={() => {
          if (!reportingLoading) setShowReportWarning(false);
        }}
        addIcon
        content={`האם אתה בטוח שברצונך לדווח על ${selectedRows.length.toLocaleString()} פקודות יומן?`}
        dangerousContent="לאחר אישור הדיווח, הפקודות יסומנו כדווחו ולא יופיעו עוד ברשימת הממתינים."
        confirmLabel="אשר דיווח"
        cancelLabel="ביטול"
        confirmLoading={reportingLoading}
        onConfirm={handleReportConfirm}
      />
    </div>
  );
};
